import { S3_IMAGE_BASE_URL, API_BASE_URL, BANNER_IMAGE_ENDPOINT } from '@/config/constant';

const isAbsoluteUrl = (value: string) =>
  /^(https?:)?\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:');

/**
 * 파일 경로에서 확장자를 추출합니다.
 * @param filePath 파일 경로
 * @returns 확장자 (예: '.png', '.jpg') 또는 빈 문자열
 */
const getFileExtension = (filePath: string): string => {
  const lastDotIndex = filePath.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return '';
  }
  return filePath.substring(lastDotIndex);
};

/**
 * 파일 경로에서 확장자 앞에 썸네일 접미사를 추가합니다.
 * @param filePath 파일 경로 (예: '/product/2025-11-14/file.png')
 * @returns 썸네일 파일 경로 (예: '/product/2025-11-14/file_thumbnail.png')
 */
const addThumbnailSuffix = (filePath: string): string => {
  if (filePath.includes('_thumbnail')) {
    return filePath;
  }
  const extension = getFileExtension(filePath);
  if (!extension) {
    return `${filePath}_thumbnail`;
  }
  const pathWithoutExtension = filePath.slice(0, -extension.length);
  return `${pathWithoutExtension}_thumbnail${extension}`;
};

/**
 * 원본 이미지 URL을 생성합니다.
 * @param filePath 파일 경로 (예: '/product/2025-11-14/file.png')
 * @returns 완전한 S3 이미지 URL 또는 null
 */
export const getImageUrl = (filePath?: string | null): string | null => {
  if (!filePath) {
    return null;
  }

  // 이미 절대 URL인 경우 그대로 반환
  if (isAbsoluteUrl(filePath)) {
    return filePath;
  }

  // 로컬 자산 경로인 경우 그대로 반환 (S3 경로 추가 방지)
  if (filePath.startsWith('/assets/') || filePath.startsWith('/img/')) {
    return filePath;
  }

  // S3 경로로 변환 (S3는 경로 앞에 슬래시가 두 개 필요)
  const baseUrl = S3_IMAGE_BASE_URL.endsWith('/') ? S3_IMAGE_BASE_URL : `${S3_IMAGE_BASE_URL}/`;
  const normalizedPath = filePath.startsWith('/') ? filePath : `/${filePath}`;
  return `${baseUrl}${normalizedPath}`;
};

/**
 * 썸네일 이미지 URL을 생성합니다.
 * @param filePath 파일 경로 (예: '/product/2025-11-14/file.png')
 * @returns 완전한 S3 썸네일 이미지 URL 또는 null
 */
export const getThumbnailUrl = (filePath?: string | null): string | null => {
  if (!filePath) {
    return null;
  }

  // 이미 절대 URL인 경우 썸네일로 변환 시도
  if (isAbsoluteUrl(filePath)) {
    // S3 URL인 경우 썸네일로 변환
    if (filePath.includes('.s3.')) {
      return addThumbnailSuffix(filePath);
    }
    // 다른 절대 URL은 그대로 반환
    return filePath;
  }

  // 파일 경로에 썸네일 접미사 추가
  const thumbnailPath = addThumbnailSuffix(filePath);
  const baseUrl = S3_IMAGE_BASE_URL.endsWith('/') ? S3_IMAGE_BASE_URL : `${S3_IMAGE_BASE_URL}/`;
  const normalizedPath = thumbnailPath.startsWith('/') ? thumbnailPath : `/${thumbnailPath}`;
  return `${baseUrl}${normalizedPath}`;
};

/**
 * 이미지 URL을 해석합니다. (기존 resolveImageUrl과의 호환성을 위한 함수)
 * 원본 이미지 URL을 반환합니다.
 * @param rawPath 파일 경로 또는 이미지 URL
 * @returns 완전한 이미지 URL 또는 null
 */
export const resolveImageUrl = (rawPath?: string | null): string | null => {
  return getImageUrl(rawPath);
};

/**
 * 배너 이미지 URL을 생성합니다. (임시 - 기존 API 사용)
 * 서버 측 조치 완료 후 S3로 전환 예정
 * @param filePath 파일 경로
 * @returns 완전한 이미지 API URL 또는 null
 */
export const getBannerImageUrl = (filePath?: string | null): string | null => {
  if (!filePath) {
    return null;
  }

  // 이미 절대 URL인 경우 그대로 반환
  if (isAbsoluteUrl(filePath)) {
    return filePath;
  }

  // API_BASE_URL이 있으면 절대 경로, 없으면 상대 경로(Proxy 사용)
  const normalizedBaseUrl = API_BASE_URL ? (API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL) : '';

  const normalizedPath = filePath.startsWith('/') ? filePath : `/${filePath}`;
  const endpoint = BANNER_IMAGE_ENDPOINT.startsWith('/') ? BANNER_IMAGE_ENDPOINT.slice(1) : BANNER_IMAGE_ENDPOINT;

  // baseUrl이 있으면 슬래시 추가, 없으면(상대경로) 맨 앞 슬래시 추가
  const prefix = normalizedBaseUrl ? `${normalizedBaseUrl}/` : '/';

  return `${prefix}${endpoint}?file=${encodeURIComponent(normalizedPath)}`;
};

/**
 * 공개 이미지 URL을 생성합니다. (기존 buildPublicImageUrl과의 호환성을 위한 함수)
 * 원본 이미지 URL을 반환합니다.
 * @param filePath 파일 경로
 * @returns 완전한 S3 이미지 URL 또는 null
 * @deprecated getImageUrl을 사용하세요
 */
export const buildPublicImageUrl = (filePath?: string | null): string | null => {
  return getImageUrl(filePath);
};

/**
 * RGB 값을 HEX 컬러 문자열로 변환합니다.
 * @param r Red 값 (0-255)
 * @param g Green 값 (0-255)
 * @param b Blue 값 (0-255)
 * @returns HEX 컬러 문자열 (예: '#FF0000')
 */
const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (value: number) => {
    const hex = value.toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * 이미지에서 특정 좌표의 픽셀 컬러를 추출합니다.
 * @param imageUrl 이미지 URL
 * @param x X 좌표 (픽셀)
 * @param y Y 좌표 (픽셀)
 * @returns HEX 컬러 문자열 또는 null (실패 시)
 */
export const extractPixelColor = async (imageUrl: string, x: number, y: number): Promise<string | null> => {
  let proxyUrl = imageUrl;
  if (import.meta.env.DEV) {
    if (imageUrl.includes('dev-acepharm-s3-public-files-bucket.s3.ap-northeast-2.amazonaws.com')) {
      proxyUrl = imageUrl.replace('https://dev-acepharm-s3-public-files-bucket.s3.ap-northeast-2.amazonaws.com', '/s3-dev');
    } else if (imageUrl.includes('acepharm-s3-public-files-bucket.s3.ap-northeast-2.amazonaws.com')) {
      proxyUrl = imageUrl.replace('https://acepharm-s3-public-files-bucket.s3.ap-northeast-2.amazonaws.com', '/s3-prod');
    }
  }

  return new Promise(resolve => {
    const img = new Image();
    if (proxyUrl.startsWith('http')) {
      img.crossOrigin = 'anonymous';
    }

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          console.error('Canvas context를 가져올 수 없습니다.');
          resolve(null);
          return;
        }

        ctx.drawImage(img, 0, 0);

        // 좌표가 이미지 범위를 벗어나지 않도록 조정
        const pixelX = Math.max(0, Math.min(x, img.width - 1));
        const pixelY = Math.max(0, Math.min(y, img.height - 1));

        const imageData = ctx.getImageData(pixelX, pixelY, 1, 1);
        const [r, g, b] = imageData.data;

        resolve(rgbToHex(r, g, b));
      } catch (error) {
        console.error('픽셀 컬러 추출 중 오류 발생:', error);
        resolve(null);
      }
    };

    img.onerror = () => {
      console.error('이미지 로드 실패:', imageUrl);
      resolve(null);
    };

    img.src = imageUrl;
  });
};

/**
 * 이미지에서 시작 좌표(1,1)와 끝 좌표(마지막 x, 1)의 픽셀 컬러를 추출합니다.
 * @param imageUrl 이미지 URL
 * @returns 시작 컬러와 끝 컬러를 포함한 객체 또는 null
 */
export const extractImageColors = async (
  imageUrl: string | null,
): Promise<{ startColor: string; endColor: string } | null> => {
  if (!imageUrl) {
    return null;
  }

  return new Promise(resolve => {
    const img = new Image();
    if (imageUrl.startsWith('http')) {
      img.crossOrigin = 'anonymous';
    }

    img.onload = async () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          console.error('Canvas context를 가져올 수 없습니다.');
          resolve(null);
          return;
        }

        ctx.drawImage(img, 0, 0);

        // 시작 좌표 (1, 1) - 0-based이므로 실제로는 (1, 1)
        const startX = Math.max(0, Math.min(1, img.width - 1));
        const startY = Math.max(0, Math.min(1, img.height - 1));

        // 끝 좌표 (마지막 x, 1)
        const endX = Math.max(0, img.width - 1);
        const endY = Math.max(0, Math.min(1, img.height - 1));

        // 시작 픽셀 컬러 추출
        const startImageData = ctx.getImageData(startX, startY, 1, 1);
        const [startR, startG, startB] = startImageData.data;
        const startColor = rgbToHex(startR, startG, startB);

        // 끝 픽셀 컬러 추출
        const endImageData = ctx.getImageData(endX, endY, 1, 1);
        const [endR, endG, endB] = endImageData.data;
        const endColor = rgbToHex(endR, endG, endB);

        resolve({ startColor, endColor });
      } catch (error) {
        console.error('이미지 컬러 추출 중 오류 발생:', error);
        resolve(null);
      }
    };

    img.onerror = () => {
      console.error('이미지 로드 실패:', imageUrl);
      resolve(null);
    };

    img.src = imageUrl;
  });
};

/**
 * 두 컬러로 그라데이션 배경 CSS를 생성합니다.
 * @param startColor 시작 컬러 (HEX)
 * @param endColor 끝 컬러 (HEX)
 * @param direction 그라데이션 방향 (기본값: 'to right')
 * @returns 그라데이션 CSS 문자열
 */
export const getGradientBackground = (startColor: string, endColor: string, direction: string = 'to right'): string => {
  return `linear-gradient(${direction}, ${startColor}, ${endColor})`;
};

/**
 * 이미지에서 세로 1픽셀 라인을 추출하여 가로로 반복 가능한 이미지 데이터 URL을 생성합니다.
 * @param imageUrl 이미지 URL
 * @param x 추출할 x 좌표 (기본값: 1)
 * @returns data URL 문자열 또는 null
 */
export const extractVerticalPixelLine = async (imageUrl: string | null, x: number = 1): Promise<string | null> => {
  if (!imageUrl) {
    return null;
  }

  let proxyUrl = imageUrl;
  if (import.meta.env.DEV) {
    if (imageUrl.includes('dev-acepharm-s3-public-files-bucket.s3.ap-northeast-2.amazonaws.com')) {
      proxyUrl = imageUrl.replace('https://dev-acepharm-s3-public-files-bucket.s3.ap-northeast-2.amazonaws.com', '/s3-dev');
    } else if (imageUrl.includes('acepharm-s3-public-files-bucket.s3.ap-northeast-2.amazonaws.com')) {
      proxyUrl = imageUrl.replace('https://acepharm-s3-public-files-bucket.s3.ap-northeast-2.amazonaws.com', '/s3-prod');
    }
  }

  return new Promise(resolve => {
    const img = new Image();
    if (proxyUrl.startsWith('http')) {
      img.crossOrigin = 'anonymous';
    }

    img.onload = () => {
      try {
        const sourceCanvas = document.createElement('canvas');
        sourceCanvas.width = img.width;
        sourceCanvas.height = img.height;
        const sourceCtx = sourceCanvas.getContext('2d');

        if (!sourceCtx) {
          console.error('Canvas context를 가져올 수 없습니다.');
          resolve(null);
          return;
        }

        sourceCtx.drawImage(img, 0, 0);

        // 추출할 x 좌표 (범위 체크)
        const extractX = Math.max(0, Math.min(x, img.width - 1));

        // 세로 1픽셀 라인 추출
        const imageData = sourceCtx.getImageData(extractX, 0, 1, img.height);

        // 새로운 canvas에 1픽셀 너비로 그리기
        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = 1;
        outputCanvas.height = img.height;
        const outputCtx = outputCanvas.getContext('2d');

        if (!outputCtx) {
          console.error('Output canvas context를 가져올 수 없습니다.');
          resolve(null);
          return;
        }

        // 이미지 데이터를 그대로 적용 (투명도 포함)
        outputCtx.putImageData(imageData, 0, 0);

        // data URL로 변환
        const dataUrl = outputCanvas.toDataURL('image/png');
        console.log('세로 픽셀 라인 추출 완료:', { width: 1, height: img.height, dataUrlLength: dataUrl.length });
        resolve(dataUrl);
      } catch (error) {
        console.error('세로 픽셀 라인 추출 중 오류 발생:', error);
        resolve(null);
      }
    };

    img.onerror = () => {
      console.error('이미지 로드 실패:', imageUrl);
      resolve(null);
    };

    img.src = imageUrl;
  });
};
